import React, { useEffect, useState } from "react";
import "./SelectTemplate.scss";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { CommonEnums } from "../../../../enums/common.enums";
import { TemplatesApi } from "../../../../utils/api/Templates";
import Loading from "../../../loading/Loading";
import { getImageUrl } from "../../../../utils/helpers/common";
import VidioIcon from "../../../../components/icons/VidioIcon";
import RoundTrueIcon from "../../../../components/icons/RoundTrueIcon";
import TemplateButtons from "./TemplateButtons";
import { AuthHelpers } from "../../../../utils/auth.helpers";

interface StateType {
  isLoading: boolean;
  listData: any[];
  totalPage: number;
  pageNumber: number;
}

interface Props {
  subscriptionTypeProp?: string;
  listingTypeProp?: string;
  postTypeProp?: string;
  templateTypeIdProp?: string;
  hideSelectedTemplate?: boolean;
  onSelectTemplate?: (id: string, imageUrl: string) => void;
  isFestivalPost?: boolean;
  festivalPostId?: string;
}

const SelectTemplatesList: React.FC<Props> = ({
  subscriptionTypeProp,
  listingTypeProp,
  postTypeProp,
  templateTypeIdProp,
  hideSelectedTemplate,
  onSelectTemplate,
  isFestivalPost = false,
  festivalPostId,
}) => {
  const [state, setState] = useState<StateType>({
    isLoading: false,
    listData: [],
    totalPage: 1,
    pageNumber: 1,
  });

  const [searchParams] = useSearchParams();

  const subscriptionType =
    subscriptionTypeProp ||
    searchParams.get("subscription_type") ||
    "agent_marketing";
  const listingType = listingTypeProp || "sale";
  const postType = postTypeProp || searchParams.get("post_type") || "";
  const festivalType = searchParams.get("festival_type") || "";
  const templateTypeId =
    templateTypeIdProp || searchParams.get("template_type") || "";

  const urlTemplateId = searchParams.get("template_id");

  const userRole = AuthHelpers.getUserType();

const getFestivalTemplateTypeId = () => {
  if (postType === CommonEnums.postTypes.image_post) {
    return listingType === "sale"
      ? "festival_for_sale_image_post"
      : "festival_for_rent_image_post";
  } else if (postType === CommonEnums.postTypes.story_post) {
    return listingType === "sale"
      ? "festival_for_sale_story_post"
      : "festival_for_rent_story_post";
  }

  return "";
};


  const actualTemplateTypeId = isFestivalPost
    ? getFestivalTemplateTypeId()
    : templateTypeId;

  const getImageClass = (type: string) => {
    switch (type) {
      case CommonEnums.postTypes.image_post:
        return "image-post-template";
      case CommonEnums.postTypes.video_post:
        return "video-post-template";
      case CommonEnums.postTypes.story_post:
        return "story-post-template";
      case CommonEnums.postTypes.reel_post:
        return "reel-post-template";
      default:
        return "template-image";
    }
  };

  const isVideoType = (type: string) =>
    [
      CommonEnums.postTypes.video_post,
      CommonEnums.postTypes.reel_post,
    ].includes(type);

  const getListApi = async () => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      let templatesData: any[] = [];

      const res = await TemplatesApi.getAgencyfestivalTemplates({
        festival_type: festivalType,
      });

      if (res?.data?.success) {
         const imagePostData = res.data.data["image_post"] || {};

        const festivalTemplate = imagePostData.templates?.find(
          (template: any) =>
            template.slug === "festival_for_sale_image_post"
        );

        if (festivalTemplate?.config?.length > 0) {
          let filteredConfigs = [...festivalTemplate.config];

          if (festivalType) {
            filteredConfigs = filteredConfigs.filter(
              (config: any) =>
                config.design_template?.festival_type === festivalType
            );
          }

          templatesData = filteredConfigs.map((config: any) => ({
            _id: config._id,
            name: config.design_template?.name || "Festival Template",
            template_image_ref: config.design_template?.template_image_ref,
            design_template: config.design_template,
            post_type: postType,
            festival_type: config.design_template?.festival_type,
            selected: config.design_template?._id === urlTemplateId,
            imageClass: getImageClass(postType),
            isVideo: isVideoType(postType),
            design_template_id: config.design_template?._id,
            is_announcement_config: true,
            status: "published",
          }));
        }
      } else {
        throw new Error(
          res?.data?.message || "Failed to load festival templates"
        );
      }

      setState((prev) => ({
        ...prev,
        isLoading: false,
        listData: templatesData,
        totalPage: 1,
      }));
    } catch (error: any) {
      setState((prev) => ({ ...prev, isLoading: false }));
      toast.error(error.message || "Something went wrong");
    }
  };

  useEffect(() => {
    getListApi();
  }, [
    actualTemplateTypeId,
    postType,
    listingType,
    subscriptionType,
    festivalType,
    urlTemplateId,
  ]);

  const getPreviewImage = (designTemplate: any) => {
    if (!designTemplate) return "";

    const isAgency = userRole === CommonEnums.userRole.agency;

    if (postType === CommonEnums.postTypes.image_post) {
      return isAgency
        ? designTemplate?.agency_image_preview_file_ref?.compressed_file_path
        : designTemplate?.agent_image_preview_file_ref?.compressed_file_path;
    }

    if (postType === CommonEnums.postTypes.story_post) {
      return isAgency
        ? designTemplate?.agency_story_preview_file_ref?.compressed_file_path
        : designTemplate?.agent_story_preview_file_ref?.compressed_file_path;
    }

    return "";
  };

  const onTemplateSelect = (
    selectedTemplateId: string,
    imageUrl?: string,
    designTemplateId?: string
  ) => {
    const updatedList = state.listData.map((item) => ({
      ...item,
      selected: item.design_template_id === designTemplateId,
    }));

    setState((prev) => ({ ...prev, listData: updatedList }));

    if (onSelectTemplate) {
      const templateId = designTemplateId || selectedTemplateId;
      onSelectTemplate(templateId, imageUrl || "");
    }
  };

  return (
    <div className="select-templates-list-container">
      <h3 className="step-title">Select Festival Template</h3>
      {state.isLoading ? (
        <div className="list-page-loading">
          <Loading />
        </div>
      ) : state.listData.length === 0 ? (
        <div className="data-not-found">
          <p>No festival templates found!</p>
        </div>
      ) : (
        <div className="select-template-container">
          {state.listData.map((data: any) => (
            <div className="select-template-box" key={data._id}>
              <div className={`template-image-common ${data.imageClass}`}>
                <img
                  src={getImageUrl(getPreviewImage(data.design_template))}
                  alt={data?.name || "template"}
                />
                {data.isVideo && (
                  <div className="video-icon-bg">
                    <div className="video-icon">
                      <VidioIcon />
                    </div>
                  </div>
                )}
                {data.selected && (
                  <div className="selected-pill">
                    <RoundTrueIcon />
                    <span>Selected</span>
                  </div>
                )}
              </div>
              <div className="template-box-text-contain">
                <p className="template-box-text-title">{data.name}</p>
                <TemplateButtons
                  templateData={data}
                  onTemplateSelect={() =>
                    onTemplateSelect(
                      data._id,
                      getImageUrl(getPreviewImage(data.design_template)),
                      data.design_template_id
                    )
                  }
                  festivalPostId={festivalPostId}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelectTemplatesList;
